/**
 * DOCUMENTATION:
 * Component: GeminiIntegration (Lesson Page)
 * Purpose: Teaches students how to connect a React app to Google Gemini.
 * * * FOR JUNIOR DEVELOPERS:
 * - This page uses 'SyntaxHighlighter' to show the code professionally.
 * - It explains the 'Security' of using .env variables.
 */

import React from "react";
import { Box, Typography, Card, Stack, Divider, styled, Alert } from "@mui/material";
import HubIcon from '@mui/icons-material/Hub';
import SecurityIcon from '@mui/icons-material/Security';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a0519",
    padding: theme.spacing(12, 2, 8),
    color: "white",
}));

const CodeBlock = styled(Box)(({ theme }) => ({
    backgroundColor: "#1e1e1e",
    padding: theme.spacing(3),
    borderRadius: "8px",
    fontFamily: "'Fira Code', monospace",
    fontSize: "0.85rem",
    border: "1px solid rgba(167, 139, 250, 0.3)",
    overflowX: "auto",
    color: "#dcdcdc",
}));

export default function GeminiIntegration() {
    const serviceCode = `
import { GoogleGenerativeAI } from "@google/generative-ai";

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

export const getResponse = async (prompt) => {
    const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });
    const result = await model.generateContent(prompt);
    return result.response.text();
};`;

    return (
        <PageWrapper>
            <Stack spacing={4} sx={{ maxWidth: "900px", margin: "0 auto" }}>
                {/* Header Section */}
                <Box textAlign="center">
                    <Typography variant="h3" fontWeight={900} color="#a78bfa">
                        GEMINI INTEGRATION
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.7 }}>
                        Connecting React to Google's Large Language Models
                    </Typography>
                </Box>

                {/* Conceptual Section */}
                <Card sx={{ p: 4, bgcolor: "rgba(255,255,255,0.03)", color: "white" }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <HubIcon color="secondary" />
                        <Typography variant="h5" fontWeight={700}>The Generative AI SDK</Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.8 }}>
                        Google provides the <code>@google/generative-ai</code> package. It allows us
                        to send prompts to Gemini and receive text, images, or code in return.
                        By using the <strong>Flash</strong> model, we get sub-second responses for
                        our AI Mentor.
                    </Typography>
                </Card>

                {/* Code Implementation Section */}
                <Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: "#a78bfa" }}>
                        Implementation Service
                    </Typography>
                    <CodeBlock>
                        <pre style={{ margin: 0 }}>{serviceCode}</pre>
                    </CodeBlock>
                </Box>

                {/* Security Section */}
                <Alert
                    icon={<SecurityIcon fontSize="inherit" />}
                    severity="warning"
                    sx={{ bgcolor: "rgba(255, 152, 0, 0.1)", color: "#ffb74d", border: "1px solid #ffb74d" }}
                >
                    <Typography variant="subtitle2" fontWeight={700}>Architectural Warning:</Typography>
                    Never hard-code your API Key. Always use <strong>REACT_APP_GEMINI_API_KEY</strong>
                    within a <code>.env</code> file and add it to your <code>.gitignore</code>.
                </Alert>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

                <Typography variant="body2" sx={{ textAlign: "center", opacity: 0.5 }}>
                    Module 3: AI & LLM Integration Track • ProDev Platform 2026
                </Typography>
            </Stack>
        </PageWrapper>
    );
}
